import { useState, useEffect } from 'react'
import { Layers, Hammer, CheckCircle2, Loader2, ChevronDown, Eye, Database } from 'lucide-react'
import { coverageApi } from '@/api/client'
import { showToast } from '@/store/uiStore'

interface PiiChip {
  raw: string
  key: string | null
}

interface DataSource {
  vendor: string | null
  source_header: string | null
  purpose: string | null
  verifies: string[]
  mandatory_pii: string | null
  mandatory_pii_chips: PiiChip[]
  verifiable_elements: string | null
  verifiable_elements_chips: PiiChip[]
  accepted_ids: string[]
  government_id: boolean
  consent_required: boolean
  adult_coverage: number | null
  api_version: string | null
  notes: string | null
}

interface UnifiedCountryRow {
  country_name: string
  country_code: string | null
  iso_alpha3: string | null
  region: string | null
  idv: boolean
  kyc_one_plus: boolean
  kyc_two_plus: boolean
  kyc_gov_id: boolean
  kyb_profile: boolean
  kyb_registry_docs: boolean
  kyb_ownership: boolean
  aml: boolean
  verification_type: string | null
  data_source_count: number | null
  coverage_estimate: number | null
  avg_match_rate: number | null
  avg_response_time: string | null
  recommended_vendor: string | null
  available_vendors: string | null
  api_status: string | null
  vendor_notes: string | null
  data_sources: DataSource[]
}

// Source of each field (for colour-coding)
const FIELD_SOURCE: Record<string, string> = {
  country_name: 'SUMMARY', country_code: 'Derived', iso_alpha3: 'Recommendations', region: 'KYC Summary',
  idv: 'SUMMARY', kyc_one_plus: 'SUMMARY', kyc_two_plus: 'SUMMARY', kyc_gov_id: 'SUMMARY',
  kyb_profile: 'SUMMARY', kyb_registry_docs: 'SUMMARY', kyb_ownership: 'SUMMARY', aml: 'SUMMARY',
  verification_type: 'KYC Summary', data_source_count: 'KYC Summary', coverage_estimate: 'KYC Summary',
  avg_match_rate: 'KYC Summary', avg_response_time: 'KYC Summary',
  recommended_vendor: 'Recommendations', available_vendors: 'Recommendations', api_status: 'Recommendations', vendor_notes: 'Recommendations',
  data_sources: 'KYC',
}

const SOURCE_COLOURS: Record<string, string> = {
  'SUMMARY': 'text-blue-400 bg-blue-500/10',
  'KYC Summary': 'text-teal-400 bg-teal-500/10',
  'KYC': 'text-violet-400 bg-violet-500/10',
  'Recommendations': 'text-amber-400 bg-amber-500/10',
  'Derived': 'text-gray-400 bg-gray-500/10',
}

function Flag({ on }: { on: boolean }) {
  return on
    ? <span className="text-green-400 font-bold">✓</span>
    : <span className="text-[var(--text-muted)]">—</span>
}

function SourceBadge({ field }: { field: string }) {
  const source = FIELD_SOURCE[field] || 'Derived'
  const cls = SOURCE_COLOURS[source]
  return <span className={`text-[9px] px-1 py-0.5 rounded-full ${cls}`}>{source}</span>
}

// Fields that are percentages (stored inconsistently: some as decimals 0-1, some as 0-100)
const PERCENTAGE_FIELDS = new Set(['coverage_estimate', 'avg_match_rate', 'adult_coverage'])

function formatValue(field: string, value: unknown): string {
  if (typeof value !== 'number') return String(value)
  if (PERCENTAGE_FIELDS.has(field)) {
    const pct = value <= 1 ? Math.round(value * 100) : Math.round(value)
    return `${pct}%`
  }
  return String(value)
}

function FieldCell({ field, value }: { field: string; value: unknown }) {
  return (
    <div className="py-1.5 px-2 border-b border-[var(--border-color)]">
      <div className="flex items-baseline gap-1.5 mb-0.5">
        <code className="text-[10px] font-mono text-[var(--text-secondary)]">{field}</code>
        <SourceBadge field={field} />
      </div>
      <div className="text-xs text-[var(--text-primary)] break-words">
        {typeof value === 'boolean' ? (
          <Flag on={value} />
        ) : value === null || value === undefined ? (
          <span className="text-[var(--text-muted)]">null</span>
        ) : typeof value === 'number' ? (
          <span className="font-mono">{formatValue(field, value)}</span>
        ) : (
          <span>{String(value)}</span>
        )}
      </div>
    </div>
  )
}

function DataSourceCard({ source, index }: { source: DataSource; index: number }) {
  return (
    <div className="rounded-md border border-violet-500/20 bg-violet-500/5 p-2.5">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[10px] font-bold text-violet-400">#{index + 1}</span>
        <code className="text-xs font-mono font-semibold text-[var(--text-primary)]">{source.source_header || '(unnamed)'}</code>
        {source.vendor && (
          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-secondary)]">
            {source.vendor}
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <div>
          <span className="text-[var(--text-muted)]">Verifies:</span>
          <div className="flex flex-wrap gap-1 mt-0.5">
            {source.verifies.length > 0 ? source.verifies.map(v => (
              <span key={v} className="px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400 text-[10px]">{v}</span>
            )) : <span className="text-[var(--text-muted)]">none</span>}
          </div>
        </div>
        <div>
          <span className="text-[var(--text-muted)]">Flags:</span>
          <div className="flex gap-2 mt-0.5">
            {source.consent_required && <span className="text-amber-400 text-[10px]">⚠ consent</span>}
            {source.government_id && <span className="text-blue-400 text-[10px]">🪪 gov ID</span>}
            {source.adult_coverage != null && (
              <span className="text-[var(--text-muted)] text-[10px]">
                📊 {source.adult_coverage <= 1 ? Math.round(source.adult_coverage * 100) : Math.round(source.adult_coverage)}%
              </span>
            )}
            {source.api_version && <span className="text-[var(--text-muted)] text-[10px]">API: {source.api_version}</span>}
          </div>
        </div>
        {source.accepted_ids.length > 0 && (
          <div className="col-span-2">
            <span className="text-[var(--text-muted)]">Accepted National IDs:</span>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {source.accepted_ids.map((id, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded text-[10px] bg-blue-500/10 text-blue-400">
                  {id}
                </span>
              ))}
            </div>
          </div>
        )}
        {source.mandatory_pii_chips.length > 0 && (
          <div className="col-span-2">
            <span className="text-[var(--text-muted)]">Mandatory PII:</span>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {source.mandatory_pii_chips.map((chip, i) => (
                <span
                  key={i}
                  className={`px-1.5 py-0.5 rounded text-[10px] ${
                    chip.key
                      ? 'bg-red-500/10 text-red-400'
                      : 'bg-gray-500/10 text-gray-400 italic'
                  }`}
                  title={chip.key ? `Normalised: ${chip.key}` : `Unrecognised: "${chip.raw}"`}
                >
                  {chip.key || chip.raw}
                </span>
              ))}
            </div>
            <details className="mt-1">
              <summary className="text-[9px] text-[var(--text-muted)] cursor-pointer hover:text-[var(--text-secondary)]">Raw text</summary>
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5 italic">{source.mandatory_pii}</p>
            </details>
          </div>
        )}
        {source.verifiable_elements_chips.length > 0 && (
          <div className="col-span-2">
            <span className="text-[var(--text-muted)]">Verifiable elements:</span>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {source.verifiable_elements_chips.map((chip, i) => (
                <span
                  key={i}
                  className={`px-1.5 py-0.5 rounded text-[10px] ${
                    chip.key
                      ? 'bg-green-500/10 text-green-400'
                      : 'bg-gray-500/10 text-gray-400 italic'
                  }`}
                  title={chip.key ? `Normalised: ${chip.key}` : `Unrecognised: "${chip.raw}"`}
                >
                  {chip.key || chip.raw}
                </span>
              ))}
            </div>
            <details className="mt-1">
              <summary className="text-[9px] text-[var(--text-muted)] cursor-pointer hover:text-[var(--text-secondary)]">Raw text</summary>
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5 italic">{source.verifiable_elements}</p>
            </details>
          </div>
        )}
        {source.mandatory_pii && source.mandatory_pii_chips.length === 0 && (
          <div className="col-span-2">
            <span className="text-[var(--text-muted)]">Mandatory PII (unparseable):</span>
            <p className="text-[var(--text-secondary)] mt-0.5 italic">{source.mandatory_pii}</p>
          </div>
        )}
        {source.verifiable_elements && source.verifiable_elements_chips.length === 0 && (
          <div className="col-span-2">
            <span className="text-[var(--text-muted)]">Verifiable elements (unparseable):</span>
            <p className="text-[var(--text-secondary)] mt-0.5 italic">{source.verifiable_elements}</p>
          </div>
        )}
        {source.notes && (
          <div className="col-span-2">
            <span className="text-[var(--text-muted)]">Notes:</span>
            <p className="text-[var(--text-secondary)] mt-0.5 italic">{source.notes}</p>
          </div>
        )}
      </div>
    </div>
  )
}

function CountryCard({ country, isFirst }: { country: UnifiedCountryRow; isFirst: boolean }) {
  const [expanded, setExpanded] = useState(isFirst)

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--bg-tertiary)] transition-colors"
      >
        <div className="flex items-center gap-3 text-left">
          <span className="font-mono text-xs px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)] text-[var(--text-primary)]">
            {country.country_code || '??'}
          </span>
          <span className="text-sm font-semibold text-[var(--text-primary)]">{country.country_name}</span>
          <div className="flex items-center gap-1 text-[10px]">
            {country.idv && <span className="px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400">IDV</span>}
            {country.kyc_one_plus && <span className="px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-400">1+</span>}
            {country.kyc_two_plus && <span className="px-1.5 py-0.5 rounded bg-teal-500/10 text-teal-400">2+</span>}
            {country.kyc_gov_id && <span className="px-1.5 py-0.5 rounded bg-violet-500/10 text-violet-400">Gov ID</span>}
            {country.kyb_ownership && <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400">KYB</span>}
            {country.aml && <span className="px-1.5 py-0.5 rounded bg-gray-500/10 text-gray-400">AML</span>}
          </div>
          <span className="text-[10px] text-[var(--text-muted)]">
            {country.data_sources.length} source{country.data_sources.length !== 1 ? 's' : ''}
          </span>
        </div>
        <ChevronDown size={14} className={`text-[var(--text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-[var(--border-color)] p-4 space-y-4">
          {/* Scalar fields in a compact grid */}
          <div>
            <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase mb-2">Country fields</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 border border-[var(--border-color)] rounded-lg">
              <FieldCell field="country_name" value={country.country_name} />
              <FieldCell field="country_code" value={country.country_code} />
              <FieldCell field="iso_alpha3" value={country.iso_alpha3} />
              <FieldCell field="region" value={country.region} />
              <FieldCell field="verification_type" value={country.verification_type} />
              <FieldCell field="data_source_count" value={country.data_source_count} />
              <FieldCell field="coverage_estimate" value={country.coverage_estimate} />
              <FieldCell field="avg_match_rate" value={country.avg_match_rate} />
              <FieldCell field="avg_response_time" value={country.avg_response_time} />
              <FieldCell field="idv" value={country.idv} />
              <FieldCell field="kyc_one_plus" value={country.kyc_one_plus} />
              <FieldCell field="kyc_two_plus" value={country.kyc_two_plus} />
              <FieldCell field="kyc_gov_id" value={country.kyc_gov_id} />
              <FieldCell field="kyb_profile" value={country.kyb_profile} />
              <FieldCell field="kyb_registry_docs" value={country.kyb_registry_docs} />
              <FieldCell field="kyb_ownership" value={country.kyb_ownership} />
              <FieldCell field="aml" value={country.aml} />
              <FieldCell field="recommended_vendor" value={country.recommended_vendor} />
              <FieldCell field="available_vendors" value={country.available_vendors} />
              <FieldCell field="api_status" value={country.api_status} />
            </div>
            {country.vendor_notes && (
              <div className="mt-2 p-2 rounded bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-baseline gap-1.5 mb-1">
                  <code className="text-[10px] font-mono text-[var(--text-secondary)]">vendor_notes</code>
                  <SourceBadge field="vendor_notes" />
                </div>
                <p className="text-xs text-[var(--text-primary)] italic">{country.vendor_notes}</p>
              </div>
            )}
          </div>

          {/* Data sources JSON array */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <p className="text-[10px] font-semibold text-[var(--text-muted)] uppercase">data_sources[] JSON array</p>
              <SourceBadge field="data_sources" />
              <span className="text-[10px] text-[var(--text-muted)]">{country.data_sources.length} items</span>
            </div>
            {country.data_sources.length > 0 ? (
              <div className="space-y-2">
                {country.data_sources.map((s, i) => <DataSourceCard key={i} source={s} index={i} />)}
              </div>
            ) : (
              <p className="text-xs text-[var(--text-muted)] italic">No data sources available for this country</p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────────────────

export function UnifiedSchemaDesign() {
  const [preview, setPreview] = useState<UnifiedCountryRow[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [building, setBuilding] = useState(false)
  const [built, setBuilt] = useState(false)
  const [buildResult, setBuildResult] = useState<{ rows: number } | null>(null)

  useEffect(() => {
    coverageApi.previewUnified({ countries: ['CN', 'IN', 'GB', 'AU', 'NZ'] }).then(res => {
      if (res.success && res.data) setPreview(res.data as UnifiedCountryRow[])
      setLoading(false)
    })
  }, [])

  const handleBuild = async () => {
    setBuilding(true)
    try {
      const res = await coverageApi.buildUnified()
      if (res.success && res.data) {
        setBuildResult(res.data as { rows: number })
        setBuilt(true)
        showToast('Unified table built successfully', 'success')
      } else {
        showToast(res.error || 'Build failed', 'error')
      }
    } catch {
      showToast('Build failed', 'error')
    }
    setBuilding(false)
  }

  return (
    <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-5 space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Layers size={16} className="text-amber-400" />
            <h3 className="text-sm font-semibold text-[var(--text-primary)]">Proposed Unified Table — Data Preview</h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Mockup</span>
          </div>
          <p className="text-xs text-[var(--text-muted)]">
            First 5 countries shown as they would appear in the unified <code className="text-[var(--text-primary)]">kyc_country_unified</code> table. Each field labelled with its authoritative source sheet.
          </p>
        </div>
        {!built && (
          <button
            onClick={handleBuild}
            disabled={building}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-500 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {building ? (
              <><Loader2 size={14} className="animate-spin" /> Building...</>
            ) : (
              <><Hammer size={14} /> Build this table</>
            )}
          </button>
        )}
        {built && buildResult && (
          <div className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-green-500/10 text-green-400 flex-shrink-0">
            <CheckCircle2 size={14} /> Built ({buildResult.rows} rows)
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex gap-2 flex-wrap text-[10px]">
        <span className="text-[var(--text-muted)]">Source sheets:</span>
        {['SUMMARY', 'KYC Summary', 'KYC', 'Recommendations', 'Derived'].map(s => (
          <span key={s} className={`px-2 py-0.5 rounded-full ${SOURCE_COLOURS[s]}`}>{s}</span>
        ))}
      </div>

      {/* Preview */}
      {loading ? (
        <div className="flex items-center gap-2 py-6 justify-center">
          <Loader2 size={18} className="animate-spin text-amber-400" />
          <span className="text-sm text-[var(--text-muted)]">Loading preview...</span>
        </div>
      ) : !preview || preview.length === 0 ? (
        <div className="py-8 text-center text-sm text-[var(--text-muted)]">
          <Database size={20} className="mx-auto mb-2 opacity-50" />
          No data imported yet. Use the "Import from XLSX" button above to load the Country Coverage spreadsheet.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
            <Eye size={12} />
            Showing {preview.length} sample countries (China, India, UK, Australia, New Zealand). The full unified table would contain ~168 countries.
          </div>
          {preview.map((country, i) => (
            <CountryCard key={country.country_name} country={country} isFirst={i === 0} />
          ))}
        </div>
      )}
    </div>
  )
}
